import { Box, CircularProgress } from "@mui/material";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions";
import { useTranslation } from "react-i18next";

export interface PlayerHandle {
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  setRate: (rate: number) => void;
  getState: () => { currentTime: number; duration: number; isPlaying: boolean; playbackRate: number };
}

interface WaveformPlayerProps {
  mediaUrl?: string;
  isLoading?: boolean;
  isTranscoding?: boolean;
  interactionType: string;
  redactionTimestamps?: { start: string; end: string }[];
  autoPlay?: boolean;
  onReady?: () => void;
  onTimeUpdate?: (timeSeconds: number) => void;
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseTimeSpan(ts: string): number {
  const parts = ts.split(":");
  if (parts.length !== 3) return 0;
  const hours = parseFloat(parts[0]);
  const minutes = parseFloat(parts[1]);
  const seconds = parseFloat(parts[2]);
  return hours * 3600 + minutes * 60 + seconds;
}

const SPEED_OPTIONS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

// ---------------------------------------------------------------------------
// Shared status shells (loading / transcoding / error / no-media)
// ---------------------------------------------------------------------------

const statusShell = (content: React.ReactNode) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      borderBottom: "1px solid var(--border-subtle)",
      backgroundColor: "var(--bg-raised)",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 80,
      gap: 1,
    }}
  >
    {content}
  </Box>
);

// ---------------------------------------------------------------------------
// Controls row — shared between audio and video sub-players
// ---------------------------------------------------------------------------

interface ControlsRowProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  mediaUrl: string;
  onTogglePlay: () => void;
  onCycleSpeed: () => void;
}

function ControlsRow({
  isPlaying,
  currentTime,
  duration,
  playbackRate,
  mediaUrl,
  onTogglePlay,
  onCycleSpeed,
}: ControlsRowProps) {
  const { t } = useTranslation(["common"]);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Play / Pause */}
      <Box
        component="button"
        aria-label={isPlaying ? t("common:pause") : t("common:play")}
        onClick={onTogglePlay}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "var(--accent)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "opacity 150ms ease",
          "&:hover": { opacity: 0.88 },
        }}
      >
        {isPlaying ? (
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            sx={{ width: 14, height: 14, color: "#FFFFFF" }}
          >
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </Box>
        ) : (
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            sx={{ width: 14, height: 14, color: "#FFFFFF", ml: "2px" }}
          >
            <polygon points="5,3 19,12 5,21" />
          </Box>
        )}
      </Box>

      {/* Time display */}
      <Box
        component="span"
        sx={{
          fontSize: "0.75rem",
          fontFamily: "'JetBrains Mono', monospace",
          color: "var(--text-secondary)",
          flexShrink: 0,
        }}
      >
        {formatTime(currentTime)} / {formatTime(duration)}
      </Box>

      {/* Speed chip */}
      <Box
        component="button"
        onClick={onCycleSpeed}
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "var(--text-secondary)",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-mid)",
          borderRadius: "6px",
          px: "7px",
          py: "3px",
          cursor: "pointer",
          flexShrink: 0,
          "&:hover": {
            color: "var(--text-primary)",
            borderColor: "var(--border-strong)",
          },
        }}
      >
        {playbackRate.toFixed(1)}x
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Download link */}
      <Box
        component="a"
        href={mediaUrl}
        download
        aria-label={t("common:download")}
        sx={{
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-tertiary)",
          borderRadius: "6px",
          textDecoration: "none",
          "&:hover": {
            color: "var(--text-primary)",
            backgroundColor: "var(--bg-hover)",
          },
        }}
      >
        <Box
          component="svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{ width: 15, height: 15 }}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// useWaveSurfer — shared WaveSurfer logic for audio and video sub-players
// ---------------------------------------------------------------------------

interface UseWaveSurferOptions {
  containerRef: React.RefObject<HTMLDivElement>;
  mediaRef?: React.RefObject<HTMLVideoElement>;  // only for video
  mediaUrl: string;
  redactionTimestamps?: { start: string; end: string }[];
  autoPlay?: boolean;
  onReady?: () => void;
  onTimeUpdate?: (timeSeconds: number) => void;
}

function useWaveSurfer({
  containerRef,
  mediaRef,
  mediaUrl,
  redactionTimestamps,
  autoPlay,
  onReady,
  onTimeUpdate,
}: UseWaveSurferOptions) {
  const wsRef = useRef<WaveSurfer | null>(null);
  const mountedRef = useRef(true);
  const hasAutoplayedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [waveformLoading, setWaveformLoading] = useState(true);
  const [error, setError] = useState(false);

  // Create / destroy WaveSurfer
  useEffect(() => {
    if (!containerRef.current) return;
    // For video mode, we need the video element to be mounted
    if (mediaRef && !mediaRef.current) return;

    setWaveformLoading(true);
    hasAutoplayedRef.current = false;

    const regions = RegionsPlugin.create();

    const createOptions: Parameters<typeof WaveSurfer.create>[0] = {
      container: containerRef.current,
      waveColor: "var(--text-muted)",
      progressColor: "var(--accent)",
      cursorColor: "var(--accent)",
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height: 56,
      normalize: true,
      dragToSeek: true,
      plugins: [regions],
    };

    if (mediaRef) {
      // Video mode: attach to the existing <video> element
      (createOptions as any).media = mediaRef.current;
    } else {
      // Audio mode: use MediaElement backend and load URL
      (createOptions as any).backend = "MediaElement";
    }

    const ws = WaveSurfer.create(createOptions);

    if (!mediaRef) {
      // Audio mode: explicitly load the URL
      ws.load(mediaUrl).catch(() => {
        if (mountedRef.current) {
          setError(true);
          setWaveformLoading(false);
        }
      });
    }

    ws.on("ready", () => {
      if (!mountedRef.current) return;
      setDuration(ws.getDuration());
      setWaveformLoading(false);
      onReady?.();
      if (redactionTimestamps) {
        regions.clearRegions();
        redactionTimestamps.forEach(({ start, end }) => {
          regions.addRegion({
            start: parseTimeSpan(start),
            end: parseTimeSpan(end),
            color: "rgba(255, 0, 0, 0.3)",
            drag: false,
            resize: false,
          });
        });
      }
      if (autoPlay && !hasAutoplayedRef.current) {
        hasAutoplayedRef.current = true;
        ws.play();
      }
    });

    ws.on("timeupdate", (time: number) => {
      if (mountedRef.current) {
        setCurrentTime(time);
        onTimeUpdate?.(time);
      }
    });

    ws.on("play", () => mountedRef.current && setIsPlaying(true));
    ws.on("pause", () => mountedRef.current && setIsPlaying(false));
    ws.on("finish", () => mountedRef.current && setIsPlaying(false));
    (ws as any).on("error", () => {
      if (mountedRef.current) {
        setError(true);
        setWaveformLoading(false);
      }
    });

    wsRef.current = ws;

    return () => {
      ws.destroy();
      wsRef.current = null;
    };
    // Intentionally run only when mediaUrl changes — mirrors V1 pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl]);

  const togglePlay = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    if (isPlaying) {
      ws.pause();
    } else {
      ws.play();
    }
  }, [isPlaying]);

  const cycleSpeed = useCallback(() => {
    setPlaybackRate((prev) => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
      wsRef.current?.setPlaybackRate(next);
      return next;
    });
  }, []);

  return {
    wsRef,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    setPlaybackRate,
    waveformLoading,
    error,
    togglePlay,
    cycleSpeed,
  };
}

// ---------------------------------------------------------------------------
// Shared sub-player props & styles
// ---------------------------------------------------------------------------

interface SubPlayerProps {
  mediaUrl: string;
  redactionTimestamps?: { start: string; end: string }[];
  autoPlay?: boolean;
  onReady?: () => void;
  onTimeUpdate?: (timeSeconds: number) => void;
}

const subPlayerWrapperSx = {
  px: 2,
  py: 1.25,
  borderBottom: "1px solid var(--border-subtle)",
  backgroundColor: "var(--bg-raised)",
  flexShrink: 0,
};

const loadingSpinnerSx = {
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  mb: 1,
};

const waveformContainerSx = (loading: boolean) => ({
  mb: 1,
  borderRadius: "4px",
  overflow: "hidden",
  cursor: "pointer",
  display: loading ? "none" : "block",
});

// ---------------------------------------------------------------------------
// Audio sub-player — WaveSurfer waveform
// ---------------------------------------------------------------------------

const AudioSubPlayer = forwardRef<PlayerHandle, SubPlayerProps>(function AudioSubPlayer({
  mediaUrl, redactionTimestamps, autoPlay, onReady, onTimeUpdate,
}, ref) {
  const { t } = useTranslation(["archie"]);
  const waveformRef = useRef<HTMLDivElement | null>(null);

  const {
    wsRef, isPlaying, currentTime, duration, playbackRate, setPlaybackRate,
    waveformLoading, error, togglePlay, cycleSpeed,
  } = useWaveSurfer({ containerRef: waveformRef, mediaUrl, redactionTimestamps, autoPlay, onReady, onTimeUpdate });

  useImperativeHandle(ref, () => ({
    play: () => wsRef.current?.play(),
    pause: () => wsRef.current?.pause(),
    seek: (time: number) => { if (wsRef.current) { wsRef.current.seekTo(time / (wsRef.current.getDuration() || 1)); } },
    setRate: (rate: number) => { wsRef.current?.setPlaybackRate(rate); setPlaybackRate(rate); },
    getState: () => ({ currentTime, duration, isPlaying, playbackRate }),
  }), [currentTime, duration, isPlaying, playbackRate, setPlaybackRate, wsRef]);

  if (error) {
    return statusShell(<Box component="span" sx={{ fontSize: "0.8125rem", color: "var(--text-tertiary)" }}>{t("archie:unableToLoadMedia")}</Box>);
  }

  return (
    <Box sx={subPlayerWrapperSx}>
      {waveformLoading && (
        <Box sx={loadingSpinnerSx}>
          <CircularProgress size={16} />
          <Box component="span" sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{t("archie:loadingWaveform")}</Box>
        </Box>
      )}
      <Box ref={waveformRef} sx={waveformContainerSx(waveformLoading)} />
      <ControlsRow isPlaying={isPlaying} currentTime={currentTime} duration={duration} playbackRate={playbackRate} mediaUrl={mediaUrl} onTogglePlay={togglePlay} onCycleSpeed={cycleSpeed} />
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Video sub-player — video element + WaveSurfer waveform
// ---------------------------------------------------------------------------

const VideoSubPlayer = forwardRef<PlayerHandle, SubPlayerProps>(function VideoSubPlayer({
  mediaUrl, redactionTimestamps, autoPlay, onReady, onTimeUpdate,
}, ref) {
  const { t } = useTranslation(["archie"]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);

  const {
    wsRef, isPlaying, currentTime, duration, playbackRate, setPlaybackRate,
    waveformLoading, error, togglePlay, cycleSpeed,
  } = useWaveSurfer({ containerRef: waveformRef, mediaRef: videoRef, mediaUrl, redactionTimestamps, autoPlay, onReady, onTimeUpdate });

  useImperativeHandle(ref, () => ({
    play: () => wsRef.current?.play(),
    pause: () => wsRef.current?.pause(),
    seek: (time: number) => { if (wsRef.current) { wsRef.current.seekTo(time / (wsRef.current.getDuration() || 1)); } },
    setRate: (rate: number) => { wsRef.current?.setPlaybackRate(rate); setPlaybackRate(rate); },
    getState: () => ({ currentTime, duration, isPlaying, playbackRate }),
  }), [currentTime, duration, isPlaying, playbackRate, setPlaybackRate, wsRef]);

  if (error) {
    return statusShell(<Box component="span" sx={{ fontSize: "0.8125rem", color: "var(--text-tertiary)" }}>{t("archie:unableToLoadMedia")}</Box>);
  }

  return (
    <Box sx={subPlayerWrapperSx}>
      <Box sx={{ mb: 1, borderRadius: "4px", overflow: "hidden" }}>
        <video ref={videoRef} src={mediaUrl} preload="metadata" playsInline onContextMenu={(e) => e.preventDefault()}
          style={{ width: "100%", maxHeight: "200px", borderRadius: "4px", backgroundColor: "#000", display: "block" }} />
      </Box>
      {waveformLoading && (
        <Box sx={loadingSpinnerSx}>
          <CircularProgress size={16} />
          <Box component="span" sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{t("archie:loadingWaveform")}</Box>
        </Box>
      )}
      <Box ref={waveformRef} sx={waveformContainerSx(waveformLoading)} />
      <ControlsRow isPlaying={isPlaying} currentTime={currentTime} duration={duration} playbackRate={playbackRate} mediaUrl={mediaUrl} onTogglePlay={togglePlay} onCycleSpeed={cycleSpeed} />
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Main WaveformPlayer
// ---------------------------------------------------------------------------

export default forwardRef<PlayerHandle, WaveformPlayerProps>(function WaveformPlayer({
  mediaUrl,
  isLoading = false,
  isTranscoding = false,
  interactionType,
  redactionTimestamps,
  autoPlay = false,
  onReady,
  onTimeUpdate,
}: WaveformPlayerProps, ref) {
  const { t } = useTranslation(["archie"]);
  // Loading state
  if (isLoading) {
    return statusShell(
      <>
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}
        >
          {t("archie:loadingMedia")}
        </Box>
      </>,
    );
  }

  // Transcoding state
  if (isTranscoding) {
    return statusShell(
      <>
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}
        >
          {t("archie:transcodingInProgress")}
        </Box>
      </>,
    );
  }

  // No media
  if (!mediaUrl) {
    return statusShell(
      <Box
        component="span"
        sx={{ fontSize: "0.8125rem", color: "var(--text-tertiary)" }}
      >
        {t("archie:mediaUnavailable")}
      </Box>,
    );
  }

  // Route to the correct sub-player based on interaction type
  if (interactionType === "Video" || interactionType === "AudioAndVideo") {
    return (
      <VideoSubPlayer
        ref={ref}
        mediaUrl={mediaUrl}
        redactionTimestamps={redactionTimestamps}
        autoPlay={autoPlay}
        onReady={onReady}
        onTimeUpdate={onTimeUpdate}
      />
    );
  }

  // Default: Audio waveform player
  return (
    <AudioSubPlayer
      ref={ref}
      mediaUrl={mediaUrl}
      redactionTimestamps={redactionTimestamps}
      autoPlay={autoPlay}
      onReady={onReady}
      onTimeUpdate={onTimeUpdate}
    />
  );
});
